/**
 * The four numbers a customer reconciles a request against.
 *
 * The server sends all four, but under two generations of names: the older
 * `sale_price_irr` / `charged_amount_irr` / `refunded_amount_irr`, and the
 * newer `quoted_price_irr` / `charged_irr` / `refunded_irr` plus the derived
 * `net_wallet_delta_irr`. Both sets are permanent on the server side, so
 * reading either is correct — but a portal build can be newer than the Stage
 * server it is pointed at, and in that window the newer names are simply
 * absent from the payload.
 *
 * Absent must not read as zero. «۰ ریال کسر شد» is a claim about the money;
 * the older names carry the same fact, so we fall back to them rather than
 * printing a number nobody computed.
 */
import type { ApiRequestRow } from '../../api/portal'

export interface RequestMoney {
  /** What the price list said this call would cost when it was accepted. */
  quotedIrr: number
  /** What actually left the wallet. Zero for a released hold. */
  chargedIrr: number
  /** What came back afterwards, if anything. */
  refundedIrr: number
  /**
   * The signed effect on the balance, in the ledger's own convention:
   * negative means money left the wallet, zero means the call was free of
   * charge in the end.
   */
  netIrr: number
}

export function requestMoney(row: ApiRequestRow): RequestMoney {
  const quotedIrr = row.quoted_price_irr ?? row.sale_price_irr
  const chargedIrr = row.charged_irr ?? row.charged_amount_irr
  const refundedIrr = row.refunded_irr ?? row.refunded_amount_irr
  return {
    quotedIrr,
    chargedIrr,
    refundedIrr,
    // Recomputed rather than trusted blindly only when the server did not
    // send it; when it did, the server's own arithmetic is authoritative.
    netIrr: row.net_wallet_delta_irr ?? refundedIrr - chargedIrr,
  }
}
